import { useCallback, useMemo, useState } from 'react'

import { faSearch } from '@fortawesome/pro-regular-svg-icons'
import { MultiSelect, Select, Input, HStack, Icon, T } from 'govwell-ui'
import { SelectBaseProps, SelectModeProps, SelectOption } from 'govwell-ui/components/Select/types'
import { AddressEntryType, EntityLayerFragment, SearchEntityVersionFragment } from 'types/graphql'

import CreatableStringSelect from 'src/components/Selects/CreatableStringSelect'
import { SelectValue } from 'src/components/Selects/EntityVersionSearch/types'
import useDebounce from 'src/hooks/use-debounce'
import {
  useMyOrgAddressEntityLayerId,
  useMyOrgParcelEntityLayerId,
} from 'src/layouts/AppStateContextLayout/utils'
import { filterNil } from 'src/utils'

import { useSearchEntityVersionsQuery } from '../../../fetch/entityVersions'

type Props = SelectModeProps<SelectValue> & {
  caption?: React.ReactNode
  entityLayers: EntityLayerFragment[]
  errorMessage?: string
  isClearable?: boolean
  isInvalid?: boolean
  isRequired?: boolean
  onBlur?: () => void
  width?: React.CSSProperties['width']
  placeholder?: string
  isLoadingEntityLayers?: boolean
  disabledEntityIds?: Set<number>
  addressEntryType: AddressEntryType
}

const EntityVersionSearchSelect = (props: Props) => {
  const {
    entityLayers,
    errorMessage,
    isClearable,
    isInvalid,
    isMultiSelect,
    isRequired,
    width,
    onValueChange,
    value,
    isLoadingEntityLayers,
    placeholder,
    disabledEntityIds,
    addressEntryType,
    onBlur,
  } = props

  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query)
  const { entityVersions, isFetching, isFetched } = useSearchEntityVersionsQuery({
    query: debouncedQuery,
    entityLayerIds: entityLayers.map((el) => el.id),
  })
  const addressLayer = useMyOrgAddressEntityLayerId()
  const parcelLayer = useMyOrgParcelEntityLayerId()
  const isMissingLayer = !addressLayer || !parcelLayer

  const handleSelectedOptionsChange = useCallback(
    (newSelectedOptions: SelectOption<SelectValue>[] | undefined) => {
      if (isMultiSelect) {
        onValueChange(newSelectedOptions?.map((o) => o.value) ?? [])
        setQuery('')
      }
    },
    [isMultiSelect, onValueChange, setQuery]
  )

  const handleSelectedOptionChange = useCallback(
    (newSelectedOption: SelectOption<SelectValue> | undefined) => {
      if (!isMultiSelect) {
        onValueChange(newSelectedOption?.value)
        setQuery('')
      }
    },
    [isMultiSelect, onValueChange, setQuery]
  )

  const valueAsArray = useMemo(() => (Array.isArray(value) ? value : filterNil([value])), [value])

  const isValidNewOption = useCallback((inputValue: string, opts: SelectOption<SelectValue>[]) => {
    const normalized = inputValue.trim().toLowerCase()
    return (
      !!normalized &&
      !opts.some((opt) => {
        const labelMatch =
          typeof opt.label === 'string' && opt.label.trim().toLowerCase() === normalized
        const valueMatch =
          typeof opt.value === 'string' && opt.value.trim().toLowerCase() === normalized
        return labelMatch || valueMatch
      })
    )
  }, [])

  const options = useMemo(() => {
    const manualStrings = valueAsArray.filter((v): v is string => typeof v === 'string')
    const selectedEntityVersionIds = new Set<number>(
      valueAsArray
        .filter((v): v is SearchEntityVersionFragment => typeof v !== 'string')
        .flatMap((u) => u.id || [])
    )
    const nonSelectedEntityVersionsInSearchResults = entityVersions?.filter(
      (e: SearchEntityVersionFragment) => !selectedEntityVersionIds.has(e.id || 0)
    )
    const entityVersionsToShow = [
      ...(valueAsArray.filter((v): v is SearchEntityVersionFragment => typeof v !== 'string') ??
        []),
      ...(nonSelectedEntityVersionsInSearchResults ?? []),
    ]
    const entityVersionOptions: SelectOption<SelectValue>[] = entityVersionsToShow.map(
      (entityVersion) => {
        const layerType = entityVersion.entity?.layer?.type
        let sublabel = undefined

        // Get searchable field values as sublabels
        const searchableFieldLabels: string[] = []

        // Get searchable field values from the searchableFields property
        if (entityVersion.searchableFields) {
          entityVersion.searchableFields
            .filter(
              (field) =>
                field.stringifiedValue &&
                field.stringifiedValue.trim() !== '' &&
                field.entityFieldTemplate?.label
            )
            .forEach((field) => {
              if (field.entityFieldTemplate?.label) {
                searchableFieldLabels.push(
                  `${field.entityFieldTemplate.label}: ${field.stringifiedValue}`
                )
              }
            })
        }

        if (layerType === 'Address' && entityVersion.intersectingParcels) {
          if (entityVersion.intersectingParcels.length === 1) {
            const parcelName = entityVersion.intersectingParcels[0]?.name
            if (parcelName) {
              searchableFieldLabels.unshift(parcelName)
            }
          } else if (entityVersion.intersectingParcels.length > 1) {
            searchableFieldLabels.unshift(`${entityVersion.intersectingParcels.length} parcels`)
          }
        }
        if (layerType === 'Parcel' && entityVersion.intersectingAddresses) {
          if (entityVersion.intersectingAddresses.length === 1) {
            const addressName = entityVersion.intersectingAddresses[0]?.name
            if (addressName) {
              searchableFieldLabels.unshift(addressName)
            }
          } else if (entityVersion.intersectingAddresses.length > 1) {
            searchableFieldLabels.unshift(`${entityVersion.intersectingAddresses.length} addresses`)
          }
        }

        // Join all sublabels with semicolon
        sublabel = searchableFieldLabels.length > 0 ? searchableFieldLabels.join('; ') : undefined

        let mainLabel = entityVersion.entity?.name

        // If there is only 1 layer for this org, customize the mainLabel
        if (isMissingLayer) {
          let displayName = ''
          if (layerType === 'Parcel') {
            // Find the field where entityFieldTemplate.isAddressName = true
            const addressNameField = entityVersion.searchableFields?.find(
              (field) => field.entityFieldTemplate?.isAddressDisplayName
            )
            if (addressNameField?.stringifiedValue) {
              displayName = addressNameField.stringifiedValue
            }
          } else if (layerType === 'Address') {
            // Find the field where entityFieldTemplate.isParcelName = true
            const parcelNameField = entityVersion.searchableFields?.find(
              (field) => field.entityFieldTemplate?.isParcelDisplayName
            )
            if (parcelNameField?.stringifiedValue) {
              displayName = parcelNameField.stringifiedValue
            }
          }
          if (displayName) {
            mainLabel += ' (' + displayName + ')'
          }
        }

        return {
          label: mainLabel ?? '',
          value: entityVersion,
          customDisplay: () => (
            <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
              <T.Body className="row-span-2 text-neutral-700">
                {layerType === 'Address' ? <Icon.FaHouse /> : <Icon.FaSquareDashed />}
              </T.Body>
              <T.Body>{mainLabel}</T.Body>
              {sublabel && <T.BodySmall>{sublabel}</T.BodySmall>}
            </div>
          ),
        }
      }
    )
    const manualOptions: SelectOption<SelectValue>[] = manualStrings.map((s) => ({
      label: s,
      value: s,
      customDisplay: () => (
        <HStack gap={2}>
          <T.Body className="text-neutral-700">
            <Icon.FaUserEdit />
          </T.Body>
          <em>{s}</em>
        </HStack>
      ),
    }))
    const allOptions = [...manualOptions, ...entityVersionOptions]

    return allOptions
  }, [entityVersions, isMissingLayer, valueAsArray])

  const shouldShowManualOption =
    addressEntryType === 'SearchAndManual' &&
    !isFetching &&
    isFetched &&
    query &&
    isValidNewOption(query, options)

  const selectedOptionSingleSelect = useMemo(() => {
    if (isMultiSelect) {
      return undefined
    }
    return options.find((o) => o.value === value)
  }, [isMultiSelect, options, value])

  const selectedOptions = useMemo(
    () => options.filter((o) => valueAsArray.includes(o.value)),
    [options, valueAsArray]
  )

  const handleCreateOption = useCallback(
    (label: string) => {
      const newOption = {
        label,
        value: label,
        isCustom: true,
      } as SelectOption<SelectValue>

      if (isMultiSelect) {
        handleSelectedOptionsChange([...selectedOptions, newOption])
      } else {
        handleSelectedOptionChange(newOption)
      }
    },
    [handleSelectedOptionChange, handleSelectedOptionsChange, isMultiSelect, selectedOptions]
  )

  const getIsOptionDisabled = useCallback(
    (option: SelectOption<SelectValue>) => {
      if (typeof option.value === 'string') {
        return false
      }
      return disabledEntityIds?.has(option.value.entity?.id || 0) ?? false
    },
    [disabledEntityIds]
  )

  const getOptionKey = useCallback(
    (option: SelectOption<SelectValue>) =>
      typeof option.value === 'string' ? option.value : option.value.id,
    []
  )

  const ariaLabel = 'Entities select'

  if (addressEntryType === 'ManualOnly') {
    const commonProps = {
      isClearable,
      isRequired,
      ariaLabel,
      placeholder,
      width,
    }
    if (isMultiSelect) {
      return (
        <CreatableStringSelect
          {...commonProps}
          onValueChange={onValueChange}
          value={(Array.isArray(value) ? value : filterNil([value])) as string[]}
        />
      )
    }
    return <Input {...commonProps} onValueChange={onValueChange} value={(value as string) ?? ''} />
  }

  const commonProps: SelectBaseProps<SelectValue> = {
    ariaLabel,
    caption: props.caption,
    errorMessage,
    getIsOptionDisabled,
    getOptionKey,
    isAsync: true,
    isClearable,
    isInvalid,
    ...(shouldShowManualOption
      ? { isCreatable: true, onCreateOption: handleCreateOption }
      : {
          isCreatable: false,
        }),
    isLoading: isLoadingEntityLayers || isFetching,
    isRequired,
    onSearchQueryChange: setQuery,
    options,
    placeholder,
    prefixIcon: faSearch,
    size: 'lg',
    suffixIcon: null,
    onBlur,
    width,
  }

  if (isMultiSelect) {
    return (
      <MultiSelect
        selectedOptions={selectedOptions}
        onSelectedOptionsChange={handleSelectedOptionsChange}
        {...commonProps}
      />
    )
  }

  return (
    <Select
      onSelectedOptionChange={handleSelectedOptionChange}
      selectedOption={selectedOptionSingleSelect}
      {...commonProps}
    />
  )
}

export default React.memo(EntityVersionSearchSelect)
